import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, ChevronDown, Edit2, Trash2 } from 'lucide-react';
import { AccountNode } from '../services/apiService';

interface AccountTreeProps {
  accounts: AccountNode[];
  canWrite: boolean;
  onEdit?: (fullPath: string) => void;
  onDelete?: (fullPath: string) => void;
}

function AccountTreeNode({
  node,
  depth,
  canWrite,
  onEdit,
  onDelete,
}: {
  node: AccountNode;
  depth: number;
  canWrite: boolean;
  onEdit?: (fullPath: string) => void;
  onDelete?: (fullPath: string) => void;
}) {
  const [expanded, setExpanded] = useState(depth < 2);
  const navigate = useNavigate();
  const hasChildren = node.children && node.children.length > 0;
  const balanceBRL = node.balances?.['BRL'] ?? 0;

  const handleAccountClick = () => {
    navigate(`/extrato?account=${encodeURIComponent(node.full_path)}`);
  };

  return (
    <>
      <tr className="hover:bg-blue-50 group even:bg-gray-100">
        <td
          className={`py-1.5 pr-8 ${depth === 0 ? 'font-semibold text-gray-900' : 'text-gray-700'}`}
          style={{ paddingLeft: `${depth * 20 + 8}px` }}
        >
          <div className="flex items-center gap-1">
            {hasChildren ? (
              <button
                onClick={() => setExpanded(!expanded)}
                className="p-0.5 rounded hover:bg-gray-200"
              >
                {expanded ? (
                  <ChevronDown className="h-4 w-4 text-gray-400" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-gray-400" />
                )}
              </button>
            ) : (
              <span className="w-5" />
            )}
            <button
              onClick={handleAccountClick}
              className="truncate text-sm text-left hover:text-blue-600 hover:underline"
              title={`Ver extrato de ${node.full_path}`}
            >
              {node.account}
            </button>
          </div>
        </td>
        <td className="py-1.5 text-right whitespace-nowrap">
          {balanceBRL !== 0 && (
            <span
              className={`text-sm tabular-nums ${
                balanceBRL < 0 ? 'text-red-600' : 'text-green-700'
              }`}
            >
              {new Intl.NumberFormat('pt-BR', {
                style: 'currency',
                currency: 'BRL',
              }).format(Math.abs(balanceBRL))}
            </span>
          )}
        </td>
        <td className="py-1.5 pl-2 whitespace-nowrap">
          <div className="flex items-center gap-1">
            {canWrite && onEdit && (
              <button
                onClick={() => onEdit(node.full_path)}
                className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-gray-200"
                title="Editar conta"
              >
                <Edit2 className="h-3.5 w-3.5 text-gray-400" />
              </button>
            )}
            {canWrite && onDelete && (
              <button
                onClick={() => onDelete(node.full_path)}
                className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-gray-200"
                title="Excluir conta"
              >
                <Trash2 className="h-3.5 w-3.5 text-red-400" />
              </button>
            )}
          </div>
        </td>
      </tr>
      {expanded && hasChildren &&
        node.children.map((child) => (
          <AccountTreeNode
            key={child.full_path}
            node={child}
            depth={depth + 1}
            canWrite={canWrite}
            onEdit={onEdit}
            onDelete={onDelete}
          />
        ))
      }
    </>
  );
}

export const AccountTree: React.FC<AccountTreeProps> = ({ accounts, canWrite, onEdit, onDelete }) => {
  if (!accounts || accounts.length === 0) {
    return <p className="text-gray-500 text-sm py-4">Nenhuma conta encontrada.</p>;
  }

  return (
    <div className="flex justify-center">
      <table>
        <tbody>
          {accounts.map((node) => (
            <AccountTreeNode
              key={node.full_path}
              node={node}
              depth={0}
              canWrite={canWrite}
              onEdit={onEdit}
              onDelete={onDelete}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
};
